import {
  Box,
  Button,
  ButtonBase,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Typography,
} from "@material-ui/core";
import { CloudUpload } from "@material-ui/icons";
import Loading from "components/Loading";
import { RootState } from "config/store";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { hasAuthority } from "shared/authorization";
import { SliceStatus } from "shared/common";
import { createComment } from "shared/network/comment.api";

export type CommentFormValues = {
  tenantId: number;
  type: string;
  originalFileName: string;
  subjectType: string;
  subjectId: number;
  note: string;
  document: FileList;
  isReportVisibleByReporter?: boolean;
  isReportVisibleByCompany?: boolean;
};
type Props = {
  subjectType:
    | "ITEM_CATEGORY"
    | "ITEM_PROFILE"
    | "USER"
    | "TOOL"
    | "CONTACT"
    | "CONTRACT"
    | "COMMENT"
    | "ISSUE"
    | "ITEM"
    | "USER_PROFILE"
    | "COMPANY"
    | "PROJECT"
    | "VEHICLE"
    | "EMPLOYEE"
    | "INCOMING_INVOICE"
    | "INVOICE"
    | "MILESTONE"
    | "OFFER"
    | "WAREHOUSE"
    | "REPORT"
    | "HACCP_CHECK"
    | "";
  subjectId: string | null | undefined;
  open: boolean;
  setOpen: (open: boolean) => void;
  refetch: () => void;
};

const ItemCategoryPicture = ({ subjectType, subjectId, open, setOpen, refetch }: Props) => {
  const { t }: any = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const form = useForm<CommentFormValues>();
  const [status, setStatus] = useState<SliceStatus>("idle");
  const { watch } = form;
  const idImage = watch("document")?.[0];
  const [imgSrc, setImgSrc] = useState<string>("");

  const { selectedRelTenant } = useSelector((state: RootState) => state.authentication);
  const account = useSelector((state: RootState) => state.authentication?.account);

  const tenant = selectedRelTenant.tenant;
  const type = form.watch("type");

  const onSubmitCreate = async (values: CommentFormValues) => {
    setStatus("pending");

    try {
      if (subjectId && subjectType) {
        await createComment(
          tenant.id,
          "DOCUMENT",
          subjectType,
          subjectId,
          "",
          values.document?.[0],
          selectedRelTenant?.tenant?.isUseReport ? values.isReportVisibleByReporter : false,
          selectedRelTenant?.tenant?.isUseReport &&
            hasAuthority(account.user, account.permissions, selectedRelTenant, ["REPORT_ADMIN"])
            ? values.isReportVisibleByCompany
            : true,
        );
        setStatus("success");
        setOpen(false);
        refetch();
        form.reset();
        enqueueSnackbar(t("common:notification.save.success"), { variant: "success" });
      }
    } catch (e: any) {
      if (e.data?.status === "FILE_NOT_FOUND") {
        enqueueSnackbar(t("common:notification.FILE_NOT_FOUND"), {
          variant: "error",
        });
      } else {
        enqueueSnackbar(t("common:notification.save.failure"), { variant: "error" });
      }
      setStatus("failure");
    }
    setStatus("success");
  };

  useEffect(() => {
    if (idImage) {
      const reader = new FileReader();
      reader.onload = e => {
        const result = e.target?.result;
        if (result && typeof result === "string") {
          setImgSrc(result);
        }
      };
      reader.readAsDataURL(idImage);
    }
  }, [idImage]);

  return (
    <>
      <Loading open={status === "pending"} />
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Kép feltöltése</DialogTitle>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmitCreate)}>
            <DialogContent>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                  paddingBottom: 2,
                }}
              >
                <ButtonBase component="label" style={{ borderRadius: 10, margin: "auto" }}>
                  <input
                    style={{ display: "none" }}
                    type="file"
                    accept="image/apng, image/avif, image/gif, image/jpeg, image/png, image/webp"
                    {...form.register("document", {
                      required: {
                        value: type === "DOCUMENT",
                        message: t("personalData.formValues.fileRequired"),
                      },
                      validate: value => {
                        if (value?.[0]?.size >= 5242880) {
                          return t("validation.maxSize", {
                            size: 5,
                            unit: "MB",
                          }).toString();
                        }
                      },
                    })}
                  />
                  <Card elevation={0} style={{ padding: 8 }}>
                    <CloudUpload />
                    <Typography style={{ fontWeight: "bold" }}>Kép kiválasztása</Typography>
                  </Card>
                </ButtonBase>
              </Box>
              {form?.formState?.errors?.document?.message ? (
                <FormHelperText error style={{ textAlign: "center" }}>
                  {form.formState.errors.document.message}
                </FormHelperText>
              ) : (
                <FormHelperText style={{ textAlign: "center" }}>Maximum 5 MB</FormHelperText>
              )}
              {!!imgSrc && (
                <Box style={{ display: "flex", justifyContent: "center" }}>
                  <img
                    alt="preview"
                    src={imgSrc}
                    style={{
                      width: "auto",
                      maxHeight: 300,
                    }}
                  />
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Box display="flex" justifyContent="center" gridGap={8}>
                <Button color="primary" variant="text" onClick={() => setOpen(false)}>
                  {t("common:button.cancel")}
                </Button>
                <Button type="submit" color="primary">
                  {t("common:button.save")}
                </Button>
              </Box>
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>
    </>
  );
};
export default ItemCategoryPicture;
